import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field, StatusBadge } from "@/components/common";
import CompanySelect from "@/components/CompanySelect";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg, fmtDate, fmtDateTime, pretty, downloadDoc, isStaffRole, REQUEST_STATUS, ROLE_LABEL } from "@/lib/api";

const Pick = ({ value, onChange, options, testId, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const useCategories = () => {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/request-categories").then((r) => setCats(r.data.map((c) => [c.key, c.label]))); }, []);
  return cats;
};

function AttachPick({ tenantId, value, onChange }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => { if (tenantId) { api.get("/documents", { params: { tenant_id: tenantId } }).then((r) => setDocs(r.data)); } }, [tenantId]);
  if (!docs.length) return <p className="text-xs text-muted-foreground">No vault documents to attach. Upload them in the Vault first.</p>;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="request-attachments">
      {docs.map((d) => <label key={d.id} className="flex items-center gap-2 text-sm"><Checkbox checked={value.includes(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`request-attach-${d.id}`} />{d.title}</label>)}
    </div>
  );
}

function NewRequest({ tenantId, onClose, onDone }) {
  const { user, isStaff } = useAuth();
  const cats = useCategories();
  const [f, setF] = useState({ type: "", subject: "", description: "", priority: "normal", period: "", tenant_id: tenantId || (isStaff ? "" : user.tenant_id), attachment_ids: [] });
  const submit = async () => {
    try { await api.post("/requests", { ...f, tenant_id: f.tenant_id || null, period: f.period || null }); toast.success("Request submitted"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const canAttach = isStaff || f.tenant_id === user.tenant_id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-request-dialog">
        <DialogHeader><DialogTitle>New service request</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {isStaff && !tenantId && <Field label="Company"><CompanySelect value={f.tenant_id} onChange={(v) => setF({ ...f, tenant_id: v, attachment_ids: [] })} testId="request-company" /></Field>}
          {!isStaff && <Field label="Company"><div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium" data-testid="request-current-company">{user.company_name}</div></Field>}
          <Field label="Service category"><Pick value={f.type} onChange={(v) => setF({ ...f, type: v })} options={cats} testId="request-type" placeholder="Choose a service" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority"><Pick value={f.priority} onChange={(v) => setF({ ...f, priority: v })} options={["low", "normal", "high", "urgent"].map((p) => [p, pretty(p)])} testId="request-priority" /></Field>
            <Field label="Period (optional)"><Input placeholder="e.g. 2026-09 or FY2026" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="request-period" /></Field>
          </div>
          <Field label="Subject"><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="request-subject" /></Field>
          <Field label="Details"><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="request-description" /></Field>
          <Field label="Attach vault documents">{canAttach && f.tenant_id
            ? <AttachPick tenantId={f.tenant_id} value={f.attachment_ids} onChange={(v) => setF({ ...f, attachment_ids: v })} />
            : <p className="text-xs text-muted-foreground">Switch to this company to attach its documents.</p>}</Field>
          <p className="text-xs text-muted-foreground">Whether a service is covered by your plan is confirmed by V.A. Services not included may be billed separately.</p>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.type || f.subject.length < 3} data-testid="request-submit-btn">Submit request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffControls({ r, act }) {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [st, setSt] = useState({ status: r.status, note: "" });
  const assign = can(user, "requests_assign");
  useEffect(() => { if (assign) { api.get("/users").then((x) => setStaff(x.data.filter((u) => u.status === "active" && isStaffRole(u.role)))); } }, [assign]);
  return (
    <div className="space-y-3 rounded-md border p-3">
      {r.plan_coverage && <p className="text-xs" data-testid="request-plan-coverage">Plan coverage (V.A only): {r.plan_coverage.status === "listed" ? `listed in ${r.plan_coverage.plan} (“${r.plan_coverage.entitlement}”)` : "not listed in any active subscription — may be billable"}</p>}
      {assign && <Field label="Assigned V.A staff"><Pick value={r.assignee_id} onChange={(v) => act(() => api.post(`/requests/${r.id}/assign`, { assignee_id: v }))} options={staff.map((u) => [u.id, u.name])} testId="request-assignee-select" placeholder="Unassigned" /></Field>}
      <div className="grid gap-2 sm:grid-cols-[180px_1fr_auto]">
        <Pick value={st.status} onChange={(v) => setSt({ ...st, status: v })} options={(NEXT[r.status] || STAFF_REQ_STATUSES).map((s) => [s, REQUEST_STATUS[s].label])} testId="request-status-select" />
        <Input placeholder="Progress update for client (optional)" value={st.note} onChange={(e) => setSt({ ...st, note: e.target.value })} data-testid="request-status-note" />
        <Button size="sm" disabled={st.status === r.status && !st.note} onClick={() => act(() => api.patch(`/requests/${r.id}`, { status: st.status, note: st.note || null })).then(() => setSt((s) => ({ ...s, note: "" }))).finally(() => setSt((s) => ({ ...s, status: r.status })))} data-testid="request-status-save">Update</Button>
      </div>
      <QuoteForm r={r} act={act} />
    </div>
  );
}

const STAFF_REQ_STATUSES = ["open", "in_progress", "awaiting_client", "resolved", "closed"];
const NEXT = { accepted: ["accepted", "resolved", "closed"], resolved: ["resolved", "closed"], quoted: ["quoted", "closed"], cancelled: ["cancelled"], closed: ["closed"], declined: ["declined", ...STAFF_REQ_STATUSES] };

function QuoteForm({ r, act }) {
  const [q, setQ] = useState({ service_fee: "", scope: "", days: "", valid_until: "", gov_estimate: "" });
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  if (!["open", "in_progress", "awaiting_client", "declined"].includes(r.status)) return r.quote ? <p className="text-xs" data-testid="request-quote-summary">Quote TZS {Number(r.quote.service_fee).toLocaleString()} · {r.quote.response ? `${r.quote.response.accept ? "accepted" : "declined"} by ${r.quote.response.by}` : "awaiting client"}</p> : null;
  const send = () => act(() => api.post(`/requests/${r.id}/quote`, { service_fee: Number(q.service_fee), scope: q.scope, days: q.days ? Number(q.days) : null, valid_until: q.valid_until || null, gov_estimate: q.gov_estimate ? Number(q.gov_estimate) : null }));
  return (
    <div className="grid gap-2 border-t pt-3 sm:grid-cols-2" data-testid="request-quote-form">
      <span className="text-xs font-semibold sm:col-span-2">Send a quote to the client</span>
      <Input type="number" min={0} placeholder="V.A service fee (TZS)" value={q.service_fee} onChange={set("service_fee")} data-testid="quote-fee" />
      <Input type="number" min={0} placeholder="Government fee estimate (optional)" value={q.gov_estimate} onChange={set("gov_estimate")} data-testid="quote-gov" />
      <Input type="number" min={1} placeholder="Estimated days (optional)" value={q.days} onChange={set("days")} data-testid="quote-days" />
      <Input type="date" value={q.valid_until} onChange={set("valid_until")} aria-label="Valid until" data-testid="quote-valid" />
      <Input className="sm:col-span-2" placeholder="Scope of work" value={q.scope} onChange={set("scope")} data-testid="quote-scope" />
      <Button size="sm" className="sm:col-span-2" disabled={q.service_fee === "" || q.scope.length < 3} onClick={send} data-testid="quote-send">Send quote</Button>
    </div>
  );
}

function Comments({ r, act }) {
  const { isStaff } = useAuth();
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const post = () => act(() => api.post(`/requests/${r.id}/comments`, { body: comment, internal })).then(() => setComment(""));
  return (
    <div className="space-y-3 border-t pt-4">
      {r.comments.map((c) => (
        <div key={c.id} className={`rounded-md p-3 text-sm ${c.internal ? "border border-dashed border-amber-400 bg-amber-50" : isStaffRole(c.by_role) ? "bg-blue-50" : "bg-muted"}`} data-testid={`request-comment-${c.id}`}>
          <div className="mb-1 text-xs text-muted-foreground">{c.internal && <strong className="text-amber-700">INTERNAL · </strong>}{c.by_name} ({ROLE_LABEL[c.by_role]}) · {fmtDateTime(c.at)}</div>{c.body}
        </div>
      ))}
      <Textarea placeholder={internal ? "Internal V.A note (hidden from client)" : "Add a comment"} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="request-comment-input" />
      <div className="flex items-center gap-3">
        <Button size="sm" disabled={!comment.trim()} onClick={post} data-testid="request-comment-submit">{internal ? "Add internal note" : "Post comment"}</Button>
        {isStaff && <label className="flex items-center gap-2 text-xs"><Checkbox checked={internal} onCheckedChange={(v) => setInternal(!!v)} data-testid="request-internal-checkbox" /> Internal (V.A only)</label>}
      </div>
    </div>
  );
}

export function RequestDetail({ id, onClose, onChanged }) {
  const { isStaff } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/requests/${id}`).then((x) => setR(x.data)); }, [id]);
  const act = async (fn) => { try { const { data } = await fn(); setR(data); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  if (!r) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="request-detail-dialog">
        <DialogHeader>
          <p className="font-mono text-xs text-muted-foreground">{r.ref} · {r.company_name} · {r.category_label || pretty(r.type)} · {pretty(r.priority)}{r.period ? ` · ${r.period}` : ""}</p>
          <DialogTitle className="font-heading">{r.subject}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <StatusBadge kind="request" status={r.status} testId="request-detail-status" />
          <span data-testid="request-assignee">{r.assignee_name ? `Handled by ${r.assignee_name}` : "Awaiting V.A assignment"}</span>
          {isStaff && <WhatsAppButton kind="request" id={r.id} />}
        </div>
        {r.description && <p className="whitespace-pre-wrap text-sm">{r.description}</p>}
        {!!r.attachments?.length && <div className="flex flex-wrap gap-2">{r.attachments.map((a) => (
          <button key={a.id} onClick={() => downloadDoc(a.id).catch((e) => toast.error(errMsg(e)))} className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted" data-testid={`request-attachment-${a.id}`}><Paperclip className="h-3 w-3" />{a.title}</button>
        ))}</div>}
        {isStaff && <StaffControls r={r} act={act} />}
        <Comments r={r} act={act} />
      </DialogContent>
    </Dialog>
  );
}

const Filters = ({ f, setF, cats, staff }) => (
  <div className="flex flex-wrap gap-2">
    <div className="w-56"><Pick value={f.type || "all"} onChange={(v) => setF({ ...f, type: v === "all" ? "" : v })} options={[["all", "All categories"], ...cats]} testId="request-filter-type" /></div>
    <div className="w-44"><Pick value={f.status || "all"} onChange={(v) => setF({ ...f, status: v === "all" ? "" : v })} options={[["all", "All statuses"], ...Object.keys(REQUEST_STATUS).map((s) => [s, REQUEST_STATUS[s].label])]} testId="request-filter-status" /></div>
    {staff && <Button size="sm" variant={f.mine ? "default" : "outline"} onClick={() => setF({ ...f, mine: !f.mine })} data-testid="request-filter-mine">Assigned to me</Button>}
  </div>
);

export default function RequestsPanel({ tenantId }) {
  const { user, isStaff } = useAuth();
  const cats = useCategories();
  const [items, setItems] = useState([]);
  const loc = useLocation();
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if ((isStaff || user.perms?.requests) && (loc.pathname === "/requests/new" || new URLSearchParams(loc.search).get("new") === "1")) setCreating(true);
  }, [loc.pathname, loc.search, loc.key]); // eslint-disable-line
  const closeNew = () => setCreating(false);
  const [openId, setOpenId] = useState(() => new URLSearchParams(loc.search).get("open"));
  useEffect(() => { const o = new URLSearchParams(loc.search).get("open"); if (o) setOpenId(o); }, [loc.search]);
  const [f, setF] = useState({ type: "", status: "", mine: false });
  const load = () => api.get("/requests", { params: { tenant_id: tenantId || undefined, type: f.type || undefined, status: f.status || undefined, assignee_id: f.mine ? "me" : undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tenantId, f]); // eslint-disable-line
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Filters f={f} setF={setF} cats={cats} staff={isStaff} />
        {(isStaff || user.perms?.requests) && <Button onClick={() => setCreating(true)} data-testid="new-request-btn"><Plus className="mr-1 h-4 w-4" /> New request</Button>}
      </div>
      {!items.length ? <EmptyState title="No service requests" hint="Ask V.A for filings, NSSF, WCF, BOT reports, OSHA, Service Levy, licences or advice." testId="requests-empty" /> : (
        <div className="space-y-2" data-testid="service-requests-list">
          {items.map((r) => (
            <button key={r.id} onClick={() => setOpenId(r.id)} data-testid={`request-row-${r.id}`} className="flex w-full flex-col gap-2 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/40 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-semibold">{r.subject}</div>
                <div className="text-xs text-muted-foreground"><span className="font-mono">{r.ref}</span> · {r.company_name} · {r.category_label || pretty(r.type)} · {fmtDate(r.created_at)} · {r.comments.length} comments{r.assignee_name ? ` · ${r.assignee_name}` : ""}</div>
              </div>
              <StatusBadge kind="request" status={r.status} />
            </button>
          ))}
        </div>
      )}
      {creating && <NewRequest tenantId={tenantId} onClose={closeNew} onDone={load} />}
      {openId && <RequestDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
